import React from 'react';
import { motion } from 'framer-motion';
import { Building2, Shield, Award, Users, Clock, ChevronDown } from 'lucide-react';

const HeroSection = () => {
  const phases = [
    { name: 'Design' },
    { name: 'Manufacture' },
    { name: 'Construct' },
    { name: 'Install' },
    { name: 'Test' },
    { name: 'Optimize' },
    { name: 'Validation' },
    { name: 'Certification' },
  ];

  const scrollToContact = (e) => {
    e.preventDefault();
    document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToProcess = (e) => {
    e.preventDefault();
    document.querySelector('#process')?.scrollIntoView({ behavior: 'smooth' });
  };

  const highlights = [
    { icon: Award, value: '500+', label: 'Projects', suffix: 'Delivered by Team' },
    { icon: Users, value: '100+', label: 'Clients', suffix: 'North America' },
    { icon: Clock, value: '30+', label: 'Years', suffix: 'Experience' },
    { icon: Building2, value: '500-50k+', label: 'Sq Ft', suffix: 'Facilities' },
  ];

  return (
    <section id="hero" className="relative min-h-screen overflow-hidden bg-[#f0f4f8]">
      
      {/* Plain Faded Background */}
      <div className="absolute inset-0">
        <div 
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(135deg, #f0f4f8 0%, #e8eef5 50%, #f5f7fa 100%)'
          }}
        />
        
        {/* Subtle decorative gradients */}
        <div 
          className="absolute top-0 left-0 w-full h-full"
          style={{
            background: 'radial-gradient(ellipse at 20% 20%, rgba(28, 75, 138, 0.03) 0%, transparent 50%)'
          }}
        />
        <div 
          className="absolute top-0 right-0 w-full h-full"
          style={{
            background: 'radial-gradient(ellipse at 80% 80%, rgba(138, 189, 224, 0.05) 0%, transparent 50%)'
          }}
        />
        
        {/* Bottom fade to next section */}
        <div 
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(180deg, transparent 0%, transparent 70%, rgba(245, 245, 247, 1) 100%)'
          }}
        />
      </div>

      {/* CSS Keyframes */}
      <style>{`
        @keyframes shimmer {
          0% { background-position: 0% center; }
          100% { background-position: 200% center; }
        }
      `}</style>

      {/* Main Content */}
      <div className="relative z-10 min-h-screen flex flex-col">
        <div className="h-24" />

        <div className="flex-1 flex items-center justify-center px-4 md:px-8 py-4 md:py-8">
          <div className="max-w-6xl mx-auto w-full">
            
            {/* Glass Card */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease: [0.25, 0.1, 0.25, 1] }}
              className="relative rounded-[24px] md:rounded-[40px] p-5 md:p-12 lg:p-16 overflow-hidden"
              style={{
                background: 'rgba(255, 255, 255, 0.85)',
                backdropFilter: 'blur(40px)',
                WebkitBackdropFilter: 'blur(40px)',
                border: '1px solid rgba(255, 255, 255, 0.9)',
                boxShadow: `
                  0 0 0 1px rgba(255, 255, 255, 0.8) inset,
                  0 25px 60px -15px rgba(28, 75, 138, 0.15),
                  0 10px 30px -10px rgba(0, 0, 0, 0.1)
                `
              }}
            >
              {/* Inner glow */}
              <div 
                className="absolute inset-0 rounded-[24px] md:rounded-[40px] opacity-60"
                style={{
                  background: 'radial-gradient(ellipse at 50% 0%, rgba(28, 75, 138, 0.06) 0%, transparent 50%)'
                }}
              />

              {/* Headline */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="text-center mb-4 md:mb-8 relative"
              >
                <h1 className="text-2xl md:text-5xl lg:text-6xl font-bold leading-[1.1] mb-4 md:mb-8">
                  <span className="text-[#1a1a2e]">Your Partner in </span>
                  <span 
                    className="relative inline-block"
                    style={{
                      background: 'linear-gradient(135deg, #1C4B8A 0%, #2A5F9E 50%, #1C4B8A 100%)',
                      backgroundSize: '200% auto',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      animation: 'shimmer 3s linear infinite',
                    }}
                  >
                    Cleanroom
                  </span>
                  <span className="text-[#1a1a2e]"> Excellence</span>
                </h1>

                <motion.p 
                  className="text-sm md:text-xl text-[#3c3c43] max-w-4xl mx-auto leading-relaxed mb-3 md:mb-5"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5 }}
                >
                  A trusted leader in controlled environment and cleanroom technology, we deliver comprehensive solutions and services tailored to the production and development needs of the biopharmaceutical, pharmaceutical, and healthcare industries.
                </motion.p>
                
                {/* Hide second paragraph on mobile */}
                <motion.p 
                  className="hidden md:block text-base md:text-lg text-[#6e6e73] max-w-4xl mx-auto leading-relaxed"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.6 }}
                >
                  Dedicated to delivering turn-key solutions across the full lifecycle — from expert design and manufacturing, to construction, commissioning, validation, and long-term support for specialized environmental rooms, chambers, and cleanrooms — ensuring quality and compliance at every stage.
                </motion.p>
              </motion.div>

              {/* Process Steps */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
                className="flex flex-wrap justify-center gap-1.5 md:gap-3 mb-4 md:mb-8 relative"
              >
                {phases.map((phase, index) => {
                  const totalBadges = phases.length;
                  const cycleDuration = 16; // Total cycle time in seconds
                  const badgeDuration = cycleDuration / totalBadges; // Time per badge
                  
                  return (
                    <div
                      key={phase.name}
                      className="relative px-2.5 md:px-5 py-1.5 md:py-3 rounded-lg md:rounded-xl text-xs md:text-sm font-semibold text-white overflow-hidden"
                      style={{
                        background: 'linear-gradient(135deg, #1C4B8A 0%, #153A6B 100%)',
                        boxShadow: '0 4px 15px rgba(28, 75, 138, 0.25)'
                      }}
                    >
                      <div
                        className="absolute inset-0 pointer-events-none"
                        style={{
                          background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                          transform: 'translateX(-100%)',
                          animation: `badgeShineSequential ${cycleDuration}s linear infinite`,
                          animationDelay: `${index * badgeDuration}s`
                        }}
                      />
                      <span className="relative">{phase.name}</span>
                    </div>
                  );
                })}
              </motion.div>

              {/* Compliance Badges - ISO Left, cGMP Right */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
                className="flex flex-wrap justify-center gap-3 md:gap-6 mb-4 md:mb-8"
              >
                <div
                  className="flex items-center gap-2 md:gap-3 px-3 md:px-6 py-2 md:py-4 rounded-xl md:rounded-2xl"
                  style={{
                    background: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(28, 75, 138, 0.1)',
                    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.04)'
                  }}
                >
                  <div 
                    className="w-8 h-8 md:w-12 md:h-12 rounded-lg md:rounded-xl flex items-center justify-center"
                    style={{
                      background: 'linear-gradient(135deg, rgba(28, 75, 138, 0.15) 0%, rgba(28, 75, 138, 0.08) 100%)',
                      border: '1px solid rgba(28, 75, 138, 0.1)'
                    }}
                  >
                    <Building2 className="w-4 h-4 md:w-6 md:h-6 text-[#1C4B8A]" />
                  </div>
                  <span className="text-xs md:text-base font-semibold text-[#1a1a2e]">ISO 5-8 Compliant Cleanrooms</span>
                </div>

                <div
                  className="flex items-center gap-2 md:gap-3 px-3 md:px-6 py-2 md:py-4 rounded-xl md:rounded-2xl"
                  style={{
                    background: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(28, 75, 138, 0.1)',
                    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.04)'
                  }}
                >
                  <div 
                    className="w-8 h-8 md:w-12 md:h-12 rounded-lg md:rounded-xl flex items-center justify-center"
                    style={{
                      background: 'linear-gradient(135deg, rgba(28, 75, 138, 0.15) 0%, rgba(28, 75, 138, 0.08) 100%)',
                      border: '1px solid rgba(28, 75, 138, 0.1)'
                    }}
                  >
                    <Shield className="w-4 h-4 md:w-6 md:h-6 text-[#1C4B8A]" />
                  </div>
                  <span className="text-xs md:text-base font-semibold text-[#1a1a2e]">cGMP Compliant</span>
                </div>
              </motion.div>

              {/* Experience Statement */}
              <motion.p 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.85 }}
                className="text-center text-sm md:text-xl font-semibold text-[#1C4B8A] mb-4 md:mb-8"
              >
                Over 30 Years of Industry Knowledge & Experience
              </motion.p>

              {/* CTA Buttons - AEE Process (Left), Start Your Project (Right) */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.9 }}
                className="flex flex-wrap justify-center gap-3 md:gap-4"
              >
                {/* AEE Process - Left */}
                <motion.a
                  href="#process"
                  onClick={scrollToProcess}
                  className="group relative inline-flex items-center justify-center px-6 md:px-10 py-3 md:py-5 font-semibold rounded-full overflow-hidden"
                  style={{
                    background: 'linear-gradient(135deg, #1C4B8A 0%, #153A6B 100%)',
                    boxShadow: '0 8px 30px rgba(28, 75, 138, 0.4)'
                  }}
                  whileHover={{ scale: 1.03, y: -3 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <motion.div
                    className="absolute inset-0"
                    style={{
                      background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                    }}
                    animate={{ x: ['-100%', '100%'] }}
                    transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                  />
                  <span className="relative text-white text-sm md:text-lg">AEE Process</span>
                </motion.a>

                {/* Start Your Project - Right */}
                <motion.a
                  href="#contact"
                  onClick={scrollToContact}
                  className="group relative inline-flex items-center justify-center px-6 md:px-10 py-3 md:py-5 font-semibold rounded-full overflow-hidden"
                  style={{
                    background: 'linear-gradient(135deg, #1C4B8A 0%, #153A6B 100%)',
                    boxShadow: '0 8px 30px rgba(28, 75, 138, 0.4)'
                  }}
                  whileHover={{ scale: 1.03, y: -3 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <motion.div
                    className="absolute inset-0"
                    style={{
                      background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                    }}
                    animate={{ x: ['-100%', '100%'] }}
                    transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                  />
                  <span className="relative text-white text-sm md:text-lg">Start Your Project</span>
                </motion.a>
              </motion.div>
            </motion.div>

            {/* Stats */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1, duration: 0.8 }}
              className="mt-6 md:mt-10 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4"
            >
              {highlights.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.1 + index * 0.1 }}
                  className="group relative p-4 md:p-6 rounded-2xl md:rounded-3xl text-center overflow-hidden"
                  style={{
                    background: 'rgba(255, 255, 255, 0.85)',
                    backdropFilter: 'blur(20px)',
                    WebkitBackdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255, 255, 255, 0.9)',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.06)'
                  }}
                  whileHover={{ 
                    y: -5,
                    boxShadow: '0 15px 40px rgba(28, 75, 138, 0.12)',
                    borderColor: 'rgba(28, 75, 138, 0.15)',
                  }}
                >
                  <motion.div
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                    style={{
                      background: 'radial-gradient(circle at 50% 50%, rgba(28, 75, 138, 0.08) 0%, transparent 70%)'
                    }}
                  />
                  
                  <div 
                    className="relative w-10 h-10 md:w-14 md:h-14 mx-auto mb-2 md:mb-4 rounded-xl md:rounded-2xl flex items-center justify-center"
                    style={{
                      background: 'linear-gradient(135deg, rgba(28, 75, 138, 0.12) 0%, rgba(28, 75, 138, 0.06) 100%)',
                      border: '1px solid rgba(28, 75, 138, 0.1)'
                    }}
                  >
                    <item.icon className="w-5 h-5 md:w-7 md:h-7 text-[#1C4B8A]" />
                  </div>
                  
                  <motion.div 
                    className="relative text-2xl md:text-5xl font-bold mb-0.5 md:mb-1"
                    style={{
                      background: 'linear-gradient(135deg, #1C4B8A 0%, #2A5F9E 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}
                  >
                    {item.value}
                  </motion.div>
                  <div className="relative text-[#3c3c43] text-xs md:text-sm font-medium">
                    {item.label}
                  </div>
                  <div className="relative text-[#8e8e93] text-[10px] md:text-xs mt-0.5">
                    {item.suffix}
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          className="pb-8 flex justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
        >
          <motion.button
            className="flex flex-col items-center gap-2 text-[#8e8e93] hover:text-[#1C4B8A] transition-colors cursor-pointer"
            onClick={() => document.querySelector('#mission')?.scrollIntoView({ behavior: 'smooth' })}
            whileHover={{ y: 5 }}
          >
            <span className="text-xs font-semibold uppercase tracking-[0.2em]">Explore</span>
            <motion.div
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <ChevronDown className="w-6 h-6" />
            </motion.div>
          </motion.button>
        </motion.div>
      </div>

      {/* Bottom gradient */}
      <div 
        className="absolute bottom-0 left-0 right-0 h-32 pointer-events-none"
        style={{
          background: 'linear-gradient(to top, #f5f5f7 0%, transparent 100%)'
        }}
      />
    </section>
  );
};

export default HeroSection;
